import { useState, useCallback, useRef, useEffect } from 'react';

type StatusPresenca = 'PRESENTE' | 'AUSENTE' | 'ATRASADO' | 'JUSTIFICADO' | 'DISPENSADO';

/** Mapa de palavras reconhecidas → status */
const STATUS_WORDS: Record<string, StatusPresenca> = {
  'presente': 'PRESENTE',
  'sim': 'PRESENTE',
  'presença': 'PRESENTE',
  'ausente': 'AUSENTE',
  'falta': 'AUSENTE',
  'faltou': 'AUSENTE',
  'atrasado': 'ATRASADO',
  'atraso': 'ATRASADO',
  'atrasada': 'ATRASADO',
  'justificado': 'JUSTIFICADO',
  'justificada': 'JUSTIFICADO',
  'justificar': 'JUSTIFICADO',
  'justificativa': 'JUSTIFICADO',
  'dispensado': 'DISPENSADO',
  'dispensada': 'DISPENSADO',
  'dispensar': 'DISPENSADO',
};

const NAV_WORDS: Record<string, 'next' | 'prev'> = {
  'próximo': 'next',
  'proximo': 'next',
  'próxima': 'next',
  'proxima': 'next',
  'pular': 'next',
  'pula': 'next',
  'volta': 'prev',
  'voltar': 'prev',
  'anterior': 'prev',
  'volte': 'prev',
};

export type VoiceEvent =
  | { type: 'status'; status: StatusPresenca }
  | { type: 'navigate'; direction: 'next' | 'prev' }
  | { type: 'nameStatus'; name: string; status: StatusPresenca }
  | { type: 'nameOnly'; name: string };

interface UseVoiceAttendanceReturn {
  micActive: boolean;
  toggleMic: () => void;
  lastHeard: string;
  ttsActive: boolean;
  toggleTts: () => void;
  speakText: (text: string) => void;
}

/**
 * Analisa o transcript para extrair eventos de voz.
 * Prioridade:
 *   1) Navegação pura (próximo, volta)
 *   2) Comando puro (uma palavra: presente, ausente)
 *   3) Nome + Comando (última palavra é comando, o resto é nome)
 *      "Gabriel Silva Presente" → nome="Gabriel Silva", cmd=PRESENTE
 *      "Maria Clara Presente"  → nome="Maria Clara", cmd=PRESENTE
 *   4) Nome puro (nenhuma palavra é comando → tudo é nome/sobrenome)
 *      "Gabriel Silva" → navega para Gabriel Silva
 */
function parseTranscript(transcript: string): VoiceEvent | null {
  const raw = transcript.trim();
  if (!raw) return null;

  const words = raw.toLowerCase().split(/\s+/);

  // 1) Navegação pura (qualquer palavra é nav)
  for (const word of words) {
    const nav = NAV_WORDS[word];
    if (nav) return { type: 'navigate', direction: nav };
  }

  // 2) Verificar SOMENTE a ÚLTIMA palavra como possível comando
  const lastWord = words[words.length - 1].replace(/[^a-záàâãéèêíïóôõúüç]/gi, '');
  const status = STATUS_WORDS[lastWord];

  if (status) {
    if (words.length === 1) {
      // Comando puro sem nome (ex: "presente")
      return { type: 'status', status };
    } else {
      // Nome + Comando (ex: "gabriel silva presente")
      const namePart = words.slice(0, -1).join(' ');
      return { type: 'nameStatus', name: namePart, status };
    }
  }

  // 3) Nenhum comando reconhecido → tudo é nome/sobrenome
  // (ex: "Gabriel Silva", "Maria Clara", "Heloísa")
  const fullName = words.join(' ');
  if (fullName.length >= 2) {
    return { type: 'nameOnly', name: fullName };
  }

  return null;
}

export function useVoiceAttendance(
  onVoiceEvent: (event: VoiceEvent) => void,
  initialState?: { micActive?: boolean; ttsActive?: boolean },
): UseVoiceAttendanceReturn {
  const [micActive, setMicActive] = useState(initialState?.micActive ?? false);
  const [ttsActive, setTtsActive] = useState(initialState?.ttsActive ?? false);
  const [lastHeard, setLastHeard] = useState('');

  // Refs para evitar stale closures
  const micActiveRef = useRef(false);
  const onVoiceEventRef = useRef(onVoiceEvent);
  const recognitionRef = useRef<any>(null);
  const restartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { micActiveRef.current = micActive; }, [micActive]);
  useEffect(() => { onVoiceEventRef.current = onVoiceEvent; }, [onVoiceEvent]);

  // ── Criar SpeechRecognition ──
  const createRecognition = useCallback(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      console.warn('[Voice] SpeechRecognition not supported');
      return null;
    }

    const recognition = new SR();
    recognition.lang = 'pt-BR';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 5;

    recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (!event.results[i].isFinal) continue;

        // Tentar todas as alternativas
        for (let alt = 0; alt < event.results[i].length; alt++) {
          const transcript = event.results[i][alt].transcript.trim();
          console.log('[Voice] Heard:', transcript);
          setLastHeard(transcript.toLowerCase());

          const parsed = parseTranscript(transcript);
          if (parsed) {
            console.log('[Voice] Event:', parsed);
            onVoiceEventRef.current(parsed);
            forceRestart();
            return;
          }
        }
      }
    };

    recognition.onerror = (event: any) => {
      console.warn('[Voice] Error:', event.error);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setMicActive(false);
        micActiveRef.current = false;
        return;
      }
      if (micActiveRef.current) scheduleRestart(800);
    };

    recognition.onend = () => {
      if (micActiveRef.current) scheduleRestart(300);
    };

    return recognition;
  }, []);

  const scheduleRestart = useCallback((delayMs: number) => {
    if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
    restartTimerRef.current = setTimeout(() => {
      if (!micActiveRef.current) return;
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch {}
        recognitionRef.current = null;
      }
      const newRec = createRecognition();
      if (newRec) {
        recognitionRef.current = newRec;
        try { newRec.start(); } catch (e) {
          console.warn('[Voice] Restart failed:', e);
          if (micActiveRef.current) scheduleRestart(1000);
        }
      }
    }, delayMs);
  }, [createRecognition]);

  const forceRestart = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }
    if (micActiveRef.current) scheduleRestart(500);
  }, [scheduleRestart]);

  const stopRecognition = useCallback(() => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }
  }, []);

  const toggleMic = useCallback(() => {
    setMicActive(prev => {
      const next = !prev;
      micActiveRef.current = next;
      if (!next) stopRecognition();
      return next;
    });
  }, [stopRecognition]);

  useEffect(() => {
    if (micActive) {
      const rec = createRecognition();
      if (rec) {
        recognitionRef.current = rec;
        try { rec.start(); } catch {}
      }
    } else {
      stopRecognition();
    }
    return () => stopRecognition();
  }, [micActive, createRecognition, stopRecognition]);

  // ── SpeechSynthesis (TTS) ──
  const speakText = useCallback((text: string) => {
    if (!ttsActive) return;
    if (!window.speechSynthesis) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pt-BR';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    const voices = window.speechSynthesis.getVoices();
    const ptVoices = voices.filter(v => v.lang.startsWith('pt-') || v.lang === 'pt-BR');
    let femaleVoice = ptVoices.find(v => 
      v.name.toLowerCase().includes('maria') || 
      v.name.toLowerCase().includes('google') ||
      v.name.toLowerCase().includes('female') ||
      v.name.toLowerCase().includes('mulher') ||
      v.name.toLowerCase().includes('portuguese (brazil)') ||
      v.name.toLowerCase().includes('luciana') ||
      v.name.toLowerCase().includes('helena') ||
      v.name.toLowerCase().includes('zira') ||
      v.name.toLowerCase().includes('francisca')
    );
    if (!femaleVoice && ptVoices.length > 0) {
      femaleVoice = ptVoices.find(v => !v.name.toLowerCase().includes('daniel')) || ptVoices[0];
    }
    if (femaleVoice) {
      utterance.voice = femaleVoice;
    }

    window.speechSynthesis.speak(utterance);
  }, [ttsActive]);

  const toggleTts = useCallback(() => {
    setTtsActive(prev => {
      if (prev) window.speechSynthesis?.cancel();
      return !prev;
    });
  }, []);

  useEffect(() => {
    return () => {
      stopRecognition();
      window.speechSynthesis?.cancel();
    };
  }, [stopRecognition]);

  return { micActive, toggleMic, lastHeard, ttsActive, toggleTts, speakText };
}
